import { DestroyRef, Injectable, inject, signal } from '@angular/core';
import { SwUpdate, VersionEvent } from '@angular/service-worker';

/** How often to poll for a new version while the tablet sits idle at the lock screen. */
const CHECK_INTERVAL_MS = 6 * 60 * 60 * 1000; // 6 hours

/**
 * Watches for new deployed versions via the Angular service worker and
 * exposes a signal the shell can use to prompt for a reload. Kiosk tablets
 * are expected to stay open indefinitely, so this also polls periodically
 * rather than relying solely on the browser's own update checks (which only
 * run on navigation/registration).
 */
@Injectable({ providedIn: 'root' })
export class AppUpdateService {
  private readonly swUpdate = inject(SwUpdate);
  private readonly destroyRef = inject(DestroyRef);

  readonly updateAvailable = signal(false);

  constructor() {
    if (!this.swUpdate.isEnabled) {
      return;
    }

    const subscription = this.swUpdate.versionUpdates.subscribe((event: VersionEvent) => {
      if (event.type === 'VERSION_READY') {
        this.updateAvailable.set(true);
      }
    });
    this.destroyRef.onDestroy(() => subscription.unsubscribe());

    const intervalId = setInterval(() => void this.swUpdate.checkForUpdate(), CHECK_INTERVAL_MS);
    this.destroyRef.onDestroy(() => clearInterval(intervalId));
  }

  /** Activates the pending version and reloads to run it. */
  async reloadForUpdate(): Promise<void> {
    if (this.swUpdate.isEnabled) {
      await this.swUpdate.activateUpdate();
    }
    this.reloadPage();
  }

  /** Isolated for testability — jsdom doesn't allow stubbing `window.location` directly. */
  protected reloadPage(): void {
    window.location.reload();
  }
}
